import { type HubConnection, HubConnectionBuilder } from "@microsoft/signalr";
import { create } from "zustand";
import ENV_PARAMS from "utils/environment";
import { t } from "i18next";
import { toast } from "react-hot-toast";
import api from "api/api";

const DEFAULT_ENSURE_TIMEOUT_MS = 60_000;
const CONNECTION_POLL_MS = 250;

const initHubConnection = () => {
  const baseURl = ENV_PARAMS.DEV ? "" : ENV_PARAMS.PARTNER_PORTAL_API_SERVER_URL;
  return new HubConnectionBuilder()
    .withUrl(`${baseURl}/api/tempo-hub/partner`)
    .withAutomaticReconnect([0, 2000, 10000, 30000])
    .build();
};

const registerHubConnection = async (hubConnectionId: string): Promise<void> => {
  const response = await api.post<void>(`/api/hubConnections`, { hubConnectionId });
  return response.data;
};

const unregisterHubConnection = async (hubConnectionId: string): Promise<void> => {
  const response = await api.delete<void>(`/api/HubConnections/${hubConnectionId}`);
  return response.data;
};

const startHubConnection = async (
  connection: HubConnection,
  updateState: (state: Partial<HubConnectionState>) => void,
  options?: { suppressErrorToast?: boolean }
) => {
  connection.onreconnecting(() => {
    console.log("Hub connection lost, attempting to reconnect...");
    updateState({ isReconnecting: true, connectionState: "Reconnecting" });
  });

  connection.onreconnected(async (connectionId) => {
    console.log("Hub reconnected with new connection ID:", connectionId);
    if (connectionId) {
      await registerHubConnection(connectionId);
    }
    updateState({ isReconnecting: false, connectionState: "Connected", hasConnection: true });
  });

  connection.onclose((error) => {
    console.log("Hub connection closed", error);
    updateState({ hasConnection: false, isReconnecting: false, connectionState: "Disconnected" });
  });

  try {
    updateState({ connectionState: "Connecting" });
    await connection.start();
    if (connection.connectionId) {
      await registerHubConnection(connection.connectionId);
      addCloseListener(connection.connectionId);
      console.log("connected to hub provider", connection.connectionId);
      updateState({ connectionState: "Connected" });
      return true;
    }
  } catch (e) {
    if (!options?.suppressErrorToast) {
      toast.error(t("translation:common.hub.failedToConnect"));
    }
    console.error("Connection failed to the hub provider", e);
    updateState({ connectionState: "Disconnected" });
    return false;
  }
};

const waitForReadyConnectionId = (
  getState: () => Pick<HubConnectionState, "connection" | "connectionState">,
  timeoutMs: number
): Promise<string | null> => {
  const readReadyId = () => {
    const { connection, connectionState } = getState();
    // Wait until Connected so hub registration in onreconnected has finished.
    if (connectionState === "Connected" && connection?.connectionId) {
      return connection.connectionId;
    }
    return null;
  };

  return new Promise((resolve) => {
    const existing = readReadyId();
    if (existing) {
      resolve(existing);
      return;
    }

    console.log(`Hub ensure: waiting for connection (timeout ${timeoutMs}ms)...`);

    const timeoutId = window.setTimeout(() => {
      window.clearInterval(intervalId);
      const timedOutId = readReadyId();
      if (timedOutId) {
        console.log("Hub ensure: connection ready just before timeout", timedOutId);
      } else {
        console.warn(
          `Hub ensure: timed out after ${timeoutMs}ms`,
          `state=${getState().connectionState}`
        );
      }
      resolve(timedOutId);
    }, timeoutMs);

    const intervalId = window.setInterval(() => {
      const connectionId = readReadyId();
      if (connectionId) {
        window.clearTimeout(timeoutId);
        window.clearInterval(intervalId);
        console.log("Hub ensure: connection ready while waiting", connectionId);
        resolve(connectionId);
      }
    }, CONNECTION_POLL_MS);
  });
};

interface HubConnectionState {
  connection: HubConnection | null;
  hasConnection: boolean;
  isReconnecting: boolean;
  connectionState: "Disconnected" | "Connected" | "Connecting" | "Reconnecting";
  getConnectionId: () => string;
  ensureConnectionId: (timeoutMs?: number) => Promise<string | null>;
  start: (options?: { suppressErrorToast?: boolean }) => Promise<void>;
  stopConnection: () => Promise<void>;
}

export const useHubConnection = create<HubConnectionState>((set, get) => ({
  connection: null,
  hasConnection: false,
  isReconnecting: false,
  connectionState: "Disconnected",
  getConnectionId: () => {
    return get().connection?.connectionId ?? "";
  },
  ensureConnectionId: async (timeoutMs = DEFAULT_ENSURE_TIMEOUT_MS) => {
    const { connection, connectionState } = get();
    if (connectionState === "Connected" && connection?.connectionId) {
      return connection.connectionId;
    }

    console.log(`Hub ensure: no ready connectionId (state=${connectionState})`);

    if (connectionState === "Reconnecting" || connectionState === "Connecting") {
      return waitForReadyConnectionId(() => get(), timeoutMs);
    }

    console.log("Hub ensure: restarting hub connection...");
    await get().start({ suppressErrorToast: true });
    const restarted = get();
    if (restarted.connectionState === "Connected" && restarted.connection?.connectionId) {
      console.log("Hub ensure: restart succeeded", restarted.connection.connectionId);
      return restarted.connection.connectionId;
    }

    console.warn(
      "Hub ensure: restart failed",
      `state=${restarted.connectionState}`,
      `connectionId=${restarted.connection?.connectionId ?? "none"}`
    );
    return null;
  },
  start: async (options) => {
    if (get().connectionState === "Connected" && get().connection?.connectionId) {
      return;
    }

    const existingConnection = get().connection;
    if (existingConnection) {
      try {
        await existingConnection.stop();
      } catch {
        // Ignore stop failures on a dead connection before recreating it.
      }
      set({ connection: null, hasConnection: false, isReconnecting: false, connectionState: "Disconnected" });
    }

    const newConnection = initHubConnection();
    const result = await startHubConnection(newConnection, (state) => set(state), options);
    set({ connection: newConnection, hasConnection: result });
  },
  stopConnection: async () => {
    await get().connection?.stop();
    set({ connection: null, hasConnection: false, isReconnecting: false, connectionState: "Disconnected" });
  },
}));

const addCloseListener = (connectionId: string) => {
  const handleBeforeUnload = () => {
    unregisterHubConnection(connectionId);
  };
  window.addEventListener("beforeunload", handleBeforeUnload);
  return () => {
    window.removeEventListener("beforeunload", handleBeforeUnload);
  };
};
